/**
 * Pure geometry for the static mirrored bracket. The tree aligns by giving every round column the
 * same fixed track height and dividing it into equal slots: a column of N boxes gives each box a
 * `trackH / N` slot (box centred), and the connector feeding M children divides the same track into
 * M elbow elements (each `trackH / M` tall). Because the slot centres of a 2N column line up with
 * the element centres of its N-child connector, the elbows always meet the boxes — no measurement.
 *
 * Returns plain ViewStyle fragments (the project's pattern for computed, non-static layout values),
 * so components keep `style={[staticStyle, computedFragment]}` and never inline a style literal.
 */

import type { ViewStyle } from 'react-native'
import {
  BRACKET_BOX_W,
  BRACKET_BRONZE_GAP,
  BRACKET_CENTER_FINAL_H,
  BRACKET_CONNECTOR_RADIUS,
  BRACKET_CONNECTOR_W,
  BRACKET_CONNECTOR_W_MAX,
  BRACKET_LINE_W,
  BRACKET_MIN_TRACK_H,
  BRACKET_TREE_PADDING_X,
  BRACKET_TROPHY_GAP,
  BRACKET_TROPHY_SIZE,
} from '../../../components/knockout/KnockoutBracket/knockoutBracket.constants'

export type ConnectorSide = 'left' | 'right'

/** Total track height for one outer round half of `outerCount` boxes at `slot` px each, floored so
 *  the centre trophy/Final/bronze stack never clips on a shallow bracket. */
export function trackHeight(outerCount: number, slot: number): number {
  return Math.max(Math.max(outerCount, 1) * slot, BRACKET_MIN_TRACK_H)
}

/** A round column / connector track of the shared height. */
export function trackStyle(height: number): ViewStyle {
  return { height }
}

/** One box slot inside a column of `count` boxes. */
export function slotStyle(trackH: number, count: number): ViewStyle {
  return { height: trackH / Math.max(count, 1) }
}

/** One connector element (one child) inside a connector track with `childCount` children. */
export function connectorElementStyle(trackH: number, childCount: number): ViewStyle {
  return { height: trackH / Math.max(childCount, 1) }
}

/**
 * Connector strip width (the column gap) that makes the bracket span the full container width.
 * Floors at the base gap — below that the bracket overflows and scrolls horizontally on a small
 * screen — and caps at the max so the gaps don't get absurd on an ultra-wide screen.
 */
export function fillConnectorWidth(
  containerW: number,
  roundColumns: number,
  connectorCount: number,
  centerW: number,
): number {
  if (connectorCount <= 0) return BRACKET_CONNECTOR_W
  const fixed = roundColumns * BRACKET_BOX_W + centerW + BRACKET_TREE_PADDING_X * 2
  const cw = (containerW - fixed) / connectorCount
  return Math.max(BRACKET_CONNECTOR_W, Math.min(BRACKET_CONNECTOR_W_MAX, cw))
}

export interface ElbowStyles {
  top: ViewStyle
  bottom: ViewStyle
  lead: ViewStyle
}

/**
 * The three line fragments of one elbow element of height `h`: the upper hook (from the top parent
 * down to the mid-line), the lower hook (from the bottom parent up to the mid-line), and the lead
 * stub from the mid-line out to the child. `side` is which side the two parents sit on. `connectorW`
 * is the strip width (the column gap), which expands so the bracket fills the screen end-to-end.
 */
export function elbowStyles(h: number, side: ConnectorSide, connectorW: number = BRACKET_CONNECTOR_W): ElbowStyles {
  const half = connectorW / 2
  const lw = BRACKET_LINE_W
  const r = Math.min(BRACKET_CONNECTOR_RADIUS, h / 4)
  const parentsLeft = side === 'left'
  const parentEdge: ViewStyle = parentsLeft ? { left: 0 } : { right: 0 }
  const childEdge: ViewStyle = parentsLeft ? { left: half } : { right: half }

  const top: ViewStyle = {
    position: 'absolute',
    top: h / 4,
    height: h / 4,
    width: half,
    borderTopWidth: lw,
    ...(parentsLeft
      ? { borderRightWidth: lw, borderTopRightRadius: r }
      : { borderLeftWidth: lw, borderTopLeftRadius: r }),
    ...parentEdge,
  }
  const bottom: ViewStyle = {
    position: 'absolute',
    top: h / 2,
    height: h / 4,
    width: half,
    borderBottomWidth: lw,
    ...(parentsLeft
      ? { borderRightWidth: lw, borderBottomRightRadius: r }
      : { borderLeftWidth: lw, borderBottomLeftRadius: r }),
    ...parentEdge,
  }
  const lead: ViewStyle = {
    position: 'absolute',
    top: h / 2 - lw / 2,
    height: lw,
    width: half,
    ...childEdge,
  }
  return { top, bottom, lead }
}

/** A straight 1→1 connector line, vertically centred in its element of height `h`. */
export function straightStyle(h: number): ViewStyle {
  return {
    position: 'absolute',
    top: h / 2 - BRACKET_LINE_W / 2,
    left: 0,
    right: 0,
    height: BRACKET_LINE_W,
  }
}

export interface CenterStyles {
  trophy: ViewStyle
  final: ViewStyle
  bronze: ViewStyle
}

/** Absolute positions for the centre trophy / Final / bronze, stacked around the track centre. */
export function centerStyles(trackH: number): CenterStyles {
  const mid = trackH / 2
  const finalTop = mid - BRACKET_CENTER_FINAL_H / 2
  return {
    final: { position: 'absolute', top: finalTop, left: 0, right: 0, alignItems: 'center' },
    trophy: {
      position: 'absolute',
      top: finalTop - BRACKET_TROPHY_GAP - BRACKET_TROPHY_SIZE,
      left: 0,
      right: 0,
      alignItems: 'center',
    },
    bronze: {
      position: 'absolute',
      top: finalTop + BRACKET_CENTER_FINAL_H + BRACKET_BRONZE_GAP,
      left: 0,
      right: 0,
      alignItems: 'center',
    },
  }
}
